import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

export default function ExpiryWarningsBanner() {
  const [expiring, setExpiring] = useState([]);
  const [expired, setExpired] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPantry();
        if (cancelled) return;
        const soon = [];
        const past = [];
        (res.data || []).forEach((it) => {
          if (!it.expiry_date) return;
          const d = daysUntil(it.expiry_date);
          if (d == null) return;
          if (d < 0) past.push({ ...it, days: d });
          else if (d <= 7) soon.push({ ...it, days: d });
        });
        soon.sort((a, b) => a.days - b.days);
        past.sort((a, b) => a.days - b.days);
        setExpiring(soon);
        setExpired(past);
      } catch {
        // silent - banner is a nice-to-have
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = expiring.length + expired.length;
  if (total === 0) return null;

  const preview = [...expired, ...expiring].slice(0, 3);
  const hasExpired = expired.length > 0;

  return (
    <Link
      to="/pantry?expiring=1"
      className={`group mb-4 block rounded-2xl border p-4 transition-all hover:shadow-md ${
        hasExpired
          ? "border-red-200 bg-gradient-to-br from-red-50 via-white to-stone-50"
          : "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-stone-50"
      }`}
      data-testid="expiry-warnings-banner"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              hasExpired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {hasExpired ? <AlertTriangle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
          </div>
          <div>
            <p
              className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${
                hasExpired ? "text-red-700" : "text-amber-700"
              }`}
            >
              {hasExpired ? "Utløpt" : "Utløper snart"}
            </p>
            <h3
              className="text-xl sm:text-2xl font-semibold text-stone-900 mt-0.5"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="expiry-warnings-title"
            >
              {expired.length > 0 && `${expired.length} utløpt`}
              {expired.length > 0 && expiring.length > 0 && " • "}
              {expiring.length > 0 && `${expiring.length} utløper innen 7 dager`}
            </h3>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="expiry-preview-chips">
              {preview.map((it) => (
                <span
                  key={it.id}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                    it.days < 0
                      ? "bg-red-50 text-red-700 border-red-200"
                      : it.days <= 2
                      ? "bg-orange-50 text-orange-700 border-orange-200"
                      : "bg-amber-50 text-amber-800 border-amber-200"
                  }`}
                >
                  {it.name}
                  <span className="opacity-70">
                    {it.days < 0
                      ? `${Math.abs(it.days)} d siden`
                      : it.days === 0
                      ? "i dag"
                      : `om ${it.days} d`}
                  </span>
                </span>
              ))}
              {total > 3 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-stone-500">
                  +{total - 3} til
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-stone-600 group-hover:text-stone-900 transition-colors self-center">
          Se beholdning <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
